import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { fmtEur } from "@/lib/format";
import StatusBadge from "@/components/StatusBadge";
import VersionBadge from "@/components/VersionBadge";
import {
  FileText, FileClock, FileCheck, FileEdit, Send, CheckCircle2,
  XCircle, Clock, Wallet, TrendingUp, Users, AlertTriangle, Bell, Check,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

const KPI_DEFS = [
  { key: "bozza", label: "In bozza", icon: FileText, color: "slate" },
  { key: "da_approvare", label: "Da approvare", icon: FileClock, color: "orange" },
  { key: "da_correggere", label: "Da correggere", icon: FileEdit, color: "cyan" },
  { key: "approvata", label: "Approvate", icon: FileCheck, color: "blue" },
  { key: "inviata", label: "Inviate", icon: Send, color: "purple" },
  { key: "accettata", label: "Accettate", icon: CheckCircle2, color: "green" },
  { key: "rifiutata", label: "Rifiutate", icon: XCircle, color: "red" },
  { key: "scaduta", label: "Scadute", icon: Clock, color: "rose" },
];

const ICON_BG = {
  slate: "bg-slate-100 text-slate-600",
  orange: "bg-orange-100 text-orange-600",
  cyan: "bg-cyan-100 text-cyan-600",
  blue: "bg-blue-100 text-blue-600",
  purple: "bg-purple-100 text-purple-600",
  green: "bg-green-100 text-green-600",
  red: "bg-red-100 text-red-600",
  rose: "bg-rose-100 text-rose-600",
};

export default function DashboardPage() {
  const [kpi, setKpi] = useState(null);
  const [recent, setRecent] = useState([]);
  const [followups, setFollowups] = useState([]);
  const navigate = useNavigate();

  const loadFollowups = async () => {
    try {
      const { data } = await api.get("/followups?scope=mine");
      setFollowups(data);
    } catch (e) { /* silent */ }
  };

  useEffect(() => {
    (async () => {
      try {
        const [{ data: k }, { data: r }] = await Promise.all([
          api.get("/dashboard/kpi"),
          api.get("/offers"),
        ]);
        setKpi(k);
        setRecent(r.slice(0, 8));
      } catch (e) {
        console.error(e);
      }
    })();
    loadFollowups();
  }, []);

  const followupBuckets = React.useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const overdue = [];
    const todayList = [];
    const upcoming = [];
    for (const fu of followups) {
      if (fu.done) continue;
      if (!fu.due_date) continue;
      const due = new Date(fu.due_date + "T00:00:00");
      if (due < today) overdue.push(fu);
      else if (due.getTime() === today.getTime()) todayList.push(fu);
      else if ((due - today) / (24 * 3600 * 1000) <= 7) upcoming.push(fu);
    }
    return { overdue, today: todayList, upcoming };
  }, [followups]);

  const completeFollowup = async (id) => {
    try {
      await api.patch(`/followups/${id}`, { done: true });
      loadFollowups();
    } catch (e) { /* silent */ }
  };

  return (
    <div className="space-y-6 md:space-y-8">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 font-mono">Panoramica</div>
          <h1 className="font-display text-2xl md:text-4xl font-bold tracking-tight text-[#0F2C59] mt-1">Dashboard</h1>
          <p className="text-slate-500 mt-1 text-sm md:text-base">Monitora offerte, clienti e performance in tempo reale.</p>
        </div>
        <div className="flex gap-2 w-full md:w-auto">
          <Button variant="outline" onClick={() => navigate("/offerte")} data-testid="view-all-offers-btn" className="flex-1 md:flex-none">
            Tutte le offerte
          </Button>
          <Button
            onClick={() => navigate("/offerte/nuova")}
            className="bg-[#0F2C59] hover:bg-[#173F7A] flex-1 md:flex-none"
            data-testid="create-offer-btn"
          >
            + Nuova
          </Button>
        </div>
      </div>

      {/* Top KPI row - 2x2 on mobile */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6">
        <KPIBig
          label="Inviate"
          fullLabel="Valore offerte inviate"
          value={fmtEur(kpi?.totale_inviate)}
          icon={Send}
          accent="text-[#0F2C59]"
          bg="bg-[#E6F0FF]"
          testId="kpi-total-sent"
        />
        <KPIBig
          label="Accettate"
          fullLabel="Valore offerte accettate"
          value={fmtEur(kpi?.totale_accettate)}
          icon={Wallet}
          accent="text-green-700"
          bg="bg-green-50"
          testId="kpi-total-accepted"
        />
        <KPIBig
          label="Conversione"
          fullLabel="Tasso di conversione"
          value={`${kpi?.tasso_conversione ?? 0}%`}
          icon={TrendingUp}
          accent="text-[#0F2C59]"
          bg="bg-[#FEF9C3]"
          testId="kpi-conversion"
        />
        <KPIBig
          label="In scadenza"
          fullLabel="Offerte in scadenza"
          value={kpi?.in_scadenza ?? 0}
          icon={AlertTriangle}
          accent="text-orange-700"
          bg="bg-orange-50"
          testId="kpi-expiring"
        />
      </div>

      {/* Status counts */}
      <div>
        <div className="text-xs uppercase tracking-widest text-slate-500 font-mono mb-3">Stato offerte</div>
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2 md:gap-3">
          {KPI_DEFS.map((k) => (
            <button
              key={k.key}
              data-testid={`kpi-${k.key}`}
              onClick={() => navigate(`/offerte?stato=${k.key}`)}
              className="text-left bg-white border border-slate-200 rounded-lg p-3 md:p-4 hover:border-[#0F2C59] hover:shadow-sm transition-all active:scale-[0.98]"
            >
              <div className={`w-8 h-8 md:w-9 md:h-9 rounded-md flex items-center justify-center mb-2 md:mb-3 ${ICON_BG[k.color]}`}>
                <k.icon className="w-4 h-4" strokeWidth={2} />
              </div>
              <div className="text-xl md:text-2xl font-display font-bold text-[#0F2C59] leading-none">{kpi?.counts?.[k.key] ?? 0}</div>
              <div className="text-[11px] md:text-xs text-slate-500 mt-1 leading-tight">{k.label}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Follow-up widget */}
      {(followupBuckets.overdue.length + followupBuckets.today.length + followupBuckets.upcoming.length) > 0 && (
        <Card className="p-0 overflow-hidden" data-testid="dashboard-followups-widget">
          <div className="flex items-center justify-between p-4 md:p-5 border-b border-slate-200">
            <div className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-[#0F2C59]" />
              <div>
                <div className="font-display font-semibold text-base md:text-lg text-[#0F2C59]">I miei follow-up</div>
                <div className="text-xs text-slate-500">
                  {followupBuckets.overdue.length > 0 && (
                    <span className="text-red-600 font-semibold">{followupBuckets.overdue.length} in ritardo</span>
                  )}
                  {followupBuckets.overdue.length > 0 && followupBuckets.today.length > 0 && <span> · </span>}
                  {followupBuckets.today.length > 0 && (
                    <span className="text-orange-600 font-semibold">{followupBuckets.today.length} oggi</span>
                  )}
                  {(followupBuckets.overdue.length + followupBuckets.today.length) > 0 && followupBuckets.upcoming.length > 0 && <span> · </span>}
                  {followupBuckets.upcoming.length > 0 && (
                    <span>{followupBuckets.upcoming.length} nei prossimi 7 giorni</span>
                  )}
                </div>
              </div>
            </div>
            <Link to="/followups" className="text-xs md:text-sm text-[#0F2C59] hover:underline font-medium whitespace-nowrap">Gestisci →</Link>
          </div>
          <ul className="divide-y divide-slate-100 max-h-[300px] overflow-y-auto">
            {[...followupBuckets.overdue, ...followupBuckets.today, ...followupBuckets.upcoming].slice(0, 6).map((fu) => {
              const overdue = followupBuckets.overdue.includes(fu);
              const isToday = followupBuckets.today.includes(fu);
              return (
                <li key={fu.id} className="flex items-center gap-3 px-4 md:px-5 py-3 hover:bg-slate-50" data-testid={`dashboard-fu-${fu.id}`}>
                  <button
                    onClick={(e) => { e.stopPropagation(); completeFollowup(fu.id); }}
                    className="w-5 h-5 rounded border border-slate-300 hover:border-[#0F2C59] flex items-center justify-center shrink-0"
                    title="Segna come completato"
                    data-testid={`dashboard-fu-complete-${fu.id}`}
                  >
                    <Check className="w-3.5 h-3.5 opacity-0 hover:opacity-60" />
                  </button>
                  <button
                    onClick={() => navigate(`/offerte/${fu.offer_id}`)}
                    className="flex-1 min-w-0 text-left"
                  >
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-sm font-semibold text-[#0F2C59]">{fu.offer_numero || "—"}</span>
                      <span className="text-slate-500 text-sm">·</span>
                      <span className="text-sm text-slate-700 truncate">{fu.cliente_nome || "—"}</span>
                      <span className={`text-[10px] uppercase font-mono px-1.5 py-0.5 rounded-full border ${
                        overdue ? "bg-red-50 text-red-700 border-red-300" :
                        isToday ? "bg-orange-50 text-orange-700 border-orange-300" :
                        "bg-slate-50 text-slate-600 border-slate-300"
                      }`}>
                        {overdue ? `Scaduto ${fu.due_date}` : isToday ? "Oggi" : fu.due_date}
                      </span>
                    </div>
                    {fu.note && <div className="text-xs text-slate-500 truncate mt-0.5">{fu.note}</div>}
                  </button>
                </li>
              );
            })}
          </ul>
        </Card>
      )}

      {/* Recent offers */}
      <Card className="p-0 overflow-hidden">
        <div className="flex items-center justify-between p-4 md:p-5 border-b border-slate-200">
          <div>
            <div className="font-display font-semibold text-base md:text-lg text-[#0F2C59]">Ultime offerte</div>
            <div className="text-xs text-slate-500">Le più recenti</div>
          </div>
          <Link to="/offerte" className="text-xs md:text-sm text-[#0F2C59] hover:underline font-medium whitespace-nowrap">Vedi tutte →</Link>
        </div>
        {/* Mobile: card list */}
        <div className="md:hidden divide-y divide-slate-100">
          {recent.length === 0 && (
            <div className="text-center py-12 text-slate-400 text-sm">
              Nessuna offerta ancora — <Link to="/offerte/nuova" className="text-[#0F2C59] font-medium">creane una</Link>
            </div>
          )}
          {recent.map((o) => (
            <button
              key={o.id}
              onClick={() => navigate(`/offerte/${o.id}`)}
              data-testid={`dashboard-offer-row-${o.numero}`}
              className="w-full text-left px-4 py-3 hover:bg-slate-50 active:bg-slate-100 transition-colors flex items-center gap-3"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono font-medium text-[#0F2C59] text-sm">{o.numero}</span>
                  <VersionBadge version={o.versione} />
                  <StatusBadge stato={o.stato} />
                </div>
                <div className="text-sm text-slate-700 truncate mt-0.5">{o.cliente_nome || "—"}</div>
                <div className="text-[11px] text-slate-400 font-mono mt-0.5">{o.created_at?.slice(0, 10)}</div>
              </div>
              <div className="text-right font-mono text-sm font-semibold text-[#0F2C59] whitespace-nowrap">{fmtEur(o.totale)}</div>
            </button>
          ))}
        </div>
        {/* Desktop: table */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-xs uppercase tracking-wider text-slate-600 border-b border-slate-300">
                <th className="text-left px-5 py-3 font-semibold">Numero</th>
                <th className="text-left px-5 py-3 font-semibold">Cliente</th>
                <th className="text-left px-5 py-3 font-semibold">Stato</th>
                <th className="text-right px-5 py-3 font-semibold">Totale</th>
                <th className="text-left px-5 py-3 font-semibold">Data</th>
              </tr>
            </thead>
            <tbody>
              {recent.length === 0 && (
                <tr><td colSpan="5" className="text-center py-12 text-slate-400">Nessuna offerta ancora — <Link to="/offerte/nuova" className="text-[#0F2C59] font-medium">creane una</Link></td></tr>
              )}
              {recent.map((o) => (
                <tr
                  key={o.id}
                  onClick={() => navigate(`/offerte/${o.id}`)}
                  data-testid={`dashboard-offer-row-${o.numero}`}
                  className="border-b border-slate-100 hover:bg-slate-50/50 cursor-pointer transition-colors"
                >
                  <td className="px-5 py-3 font-mono font-medium text-[#0F2C59]">
                    <span className="inline-flex items-center gap-1.5">
                      {o.numero}
                      <VersionBadge version={o.versione} />
                    </span>
                  </td>
                  <td className="px-5 py-3">{o.cliente_nome || "—"}</td>
                  <td className="px-5 py-3"><StatusBadge stato={o.stato} /></td>
                  <td className="px-5 py-3 text-right font-mono">{fmtEur(o.totale)}</td>
                  <td className="px-5 py-3 text-slate-500">{o.created_at?.slice(0, 10)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function KPIBig({ label, fullLabel, value, icon: Icon, accent, bg, testId }) {
  return (
    <Card className="p-4 md:p-6" data-testid={testId}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[10px] md:text-xs uppercase tracking-wider text-slate-500 font-mono truncate">
            <span className="md:hidden">{label}</span>
            <span className="hidden md:inline">{fullLabel}</span>
          </div>
          <div className={`mt-2 md:mt-3 text-lg md:text-3xl font-display font-bold tracking-tight ${accent} leading-none break-all`}>{value}</div>
        </div>
        <div className={`w-8 h-8 md:w-11 md:h-11 rounded-lg flex items-center justify-center flex-shrink-0 ${bg}`}>
          <Icon className={`w-4 h-4 md:w-5 md:h-5 ${accent}`} strokeWidth={2} />
        </div>
      </div>
    </Card>
  );
}
